import { useEffect, useState } from 'react';
import { fetchChangelog, type ChangelogRelease } from '../api/changelog';
import { ca } from '../i18n/ca';
import { PageLoading, PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { Shell } from './Shell';

const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('ca', { dateStyle: 'long' }) : null;

/**
 * What has changed, for anybody: no account and no session, because whoever is
 * deciding whether this is worth their evening does not have one yet.
 *
 * The platform reads the repository's own CHANGELOG.md, so this page and what
 * the bot posts on Discord are the same sentences, written once.
 */
export function Changelog() {
  const [releases, setReleases] = useState<ChangelogRelease[] | null>(null);
  const [failed, setFailed] = useState<unknown>(null);
  const [attempt, retry] = useRetryKey();
  const t = ca.changelogPage;

  useEffect(() => {
    setFailed(null);
    fetchChangelog()
      .then(setReleases)
      .catch((err) => {
        setReleases([]);
        setFailed(err);
      });
  }, [attempt]);

  const heading = (release: ChangelogRelease) =>
    release.unreleased ? t.unreleased : t.version(release.version);

  return (
    <Shell title={t.title} width="wide">
      <p className="muted">{t.intro}</p>
      {releases === null && <PageLoading />}
      {failed !== null && <PageProblem error={failed} text={t.error} onRetry={retry} />}
      {releases !== null && releases.length === 0 && failed === null && (
        <div className="empty">
          <i className="bi bi-megaphone" aria-hidden="true" />
          <p className="muted">{t.empty}</p>
        </div>
      )}
      {(releases ?? []).map((release) => (
        <section key={release.version} className="card changelog" aria-label={heading(release)}>
          <div className="row between">
            <h2 className="section">{heading(release)}</h2>
            {release.unreleased ? (
              <span className="chip warn">{t.unreleasedHint}</span>
            ) : (
              release.date && <span className="muted small">{day(release.date)}</span>
            )}
          </div>
          <ul>
            {release.entries.map((entry, index) => (
              <li key={`${entry.key}/${index}`}>
                <i
                  className={`bi bi-${t.icons[entry.kind] ?? 'stars'}`}
                  aria-hidden="true"
                  title={t.kinds[entry.kind] ?? entry.kind}
                />
                <span>
                  <strong>{entry.title}</strong>
                  {entry.text && <span className="muted"> — {entry.text}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </Shell>
  );
}
